// src/hooks/useDatapointApi.tsx

import { API } from 'aws-amplify'
import {
  useState,
  useCallback,
  createContext,
  useContext,
  useEffect,
  useRef,
  useMemo,
  ReactNode,
} from 'react'

import { DashboardId, PanelId, PanelPool } from '@/types/domain'

import { authHeader, notifyError, notifySuccess } from '../api/'
import { usePreloader } from '../components/'
import { languageConstants as LC } from '../constants/'
import type {
  DatapointContextValue,
  DatapointItemsResponse,
  DatapointMap,
  DatapointPool,
  DatapointUpdatePayload,
} from '../types/domain/datapoint'

import { refreshToken } from './auth/'

const { REACT_APP_API_URL } = import.meta.env

const DatapointCtx = createContext<DatapointContextValue>({
  data: { items: {} },
  map: {},

  setMap: () => {},

  invalidate: () => {},
  inflightRef: {
    current: new Map<string, Promise<DatapointPool> | null>(),
  },
})

const keyFor = (dashboardId: DashboardId, panelId: PanelId) =>
  `${dashboardId}:${panelId}`

const fetchDatapointsRaw = async (
  dashboardId: DashboardId,
  panelId: PanelId
): Promise<DatapointPool | null> => {
  await refreshToken()

  const API_NAME = 'karmaApi'
  const endpoint = `/dashboard/${dashboardId}/panel/${panelId}/datapoint`
  const options = { method: 'GET', headers: { ...authHeader() } }
  const result = await API.get(API_NAME, endpoint, options)
  return result?.data ?? null
}

type Props = {
  children: ReactNode
  dashboardId?: DashboardId
  panels?: PanelPool
}

export const DatapointDataProvider = ({
  children,
  dashboardId,
  panels = [],
}: Props) => {
  const [data, setData] = useState<DatapointItemsResponse>({ items: {} })
  const [map, setMap] = useState<DatapointMap>({})
  const inflightRef = useRef<Map<string, Promise<DatapointPool> | null>>(
    new Map()
  )
  const mapRef = useRef<DatapointMap>(map)
  const { start, stop } = usePreloader()

  useEffect(() => {
    mapRef.current = map
  }, [map])

  useEffect(() => {
    let active = true

    const run = async () => {
      if (!dashboardId || !panels || panels.length === 0) return

      start()
      try {
        const ids = panels.map(p => Number(p.id)).filter(Boolean)
        const items = {}
        const toFetch = []

        ids.forEach(id => {
          const k = keyFor(dashboardId, id)
          const hit = mapRef.current?.[k]
          if (hit) {
            items[id] = Array.isArray(hit)
              ? hit.map(point => point?.attributes?.sensor_id)
              : []
          } else {
            toFetch.push(id)
          }
        })

        await Promise.all(
          toFetch.map(async id => {
            const k = keyFor(dashboardId, id)

            const existing = inflightRef.current.get(k)
            if (existing) {
              const result = await existing
              items[id] = Array.isArray(result)
                ? result.map(p => p?.attributes?.sensor_id)
                : []
              return
            }

            const exec = async () => fetchDatapointsRaw(dashboardId, id)

            const p = (async () => {
              try {
                return await exec()
              } catch (err) {
                const status = err?.status ?? err?.response?.status
                if (status === 401 || status === 403) {
                  await refreshToken()
                  return await exec()
                }
                throw err
              }
            })()
              .then(result => {
                if (result) {
                  setMap(prev => ({ ...prev, [k]: result }))
                  items[id] = result.map(p => p?.attributes?.sensor_id)
                } else {
                  items[id] = []
                }
                return result
              })
              .finally(() => inflightRef.current.delete(k))

            inflightRef.current.set(k, p)
            await p
          })
        )

        if (active) setData({ items })
      } catch (error) {
        if (active) notifyError(`datapoints pool error ${error?.message}`)
      } finally {
        stop()
      }
    }

    run()

    return () => {
      active = false
    }
  }, [dashboardId, panels, start, stop])

  const invalidate = useCallback((id: DashboardId, panelId: PanelId) => {
    setMap(prev => {
      const next = { ...prev }
      if (panelId === null) {
        Object.keys(next)
          .filter(k => k.startsWith(`${id}:`))
          .forEach(k => delete next[k])
      } else {
        delete next[keyFor(id, panelId)]
      }
      return next
    })
    if (panelId === null) {
      Array.from(inflightRef.current.keys())
        .filter(k => k.startsWith(`${id}:`))
        .forEach(k => inflightRef.current.delete(k))
    } else {
      inflightRef.current.delete(keyFor(id, panelId))
    }
  }, [])

  const value = useMemo(
    () => ({ data, map, setMap, invalidate, inflightRef }),
    [data, map, invalidate]
  )

  return <DatapointCtx.Provider value={value}>{children}</DatapointCtx.Provider>
}

export const useDataPointsPool = () => useContext(DatapointCtx)

export const useDatapointApi = () => {
  const { start, stop } = usePreloader()
  const { map, setMap, invalidate, inflightRef } = useContext(DatapointCtx)
  const mapRef = useRef<DatapointMap>(map)

  useEffect(() => {
    mapRef.current = map
  }, [map])

  const withRequest = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | null> => {
      try {
        return await fn()
      } catch (error) {
        const status = error?.status ?? error?.response?.status
        if (status === 401 || status === 403) {
          try {
            await refreshToken()
            return await fn()
          } catch (e2) {
            notifyError(`DatapointApi: ${e2?.message}`)
            return null
          }
        }
        notifyError(`DatapointApi: ${error?.message}`)
        return null
      }
    },
    []
  )

  const withPreloader = useCallback(
    async fn => {
      start()
      try {
        return await fn()
      } finally {
        stop()
      }
    },
    [start, stop]
  )

  const getDatapoints = useCallback(
    (dashboardId: DashboardId, panelId: PanelId): Promise<DatapointPool> =>
      withPreloader(() =>
        withRequest(async () => {
          if (!dashboardId || !panelId) return []

          const k = keyFor(dashboardId, panelId)
          const cached = mapRef.current?.[k]
          if (cached) return cached

          const inflight = inflightRef?.current?.get(k)
          if (inflight) return inflight

          const p = fetchDatapointsRaw(dashboardId, panelId)
            .then(result => {
              if (result) setMap(prev => ({ ...prev, [k]: result }))
              return result
            })
            .finally(() => inflightRef?.current?.delete(k))

          inflightRef?.current?.set(k, p)
          return p
        })
      ).then(res => (Array.isArray(res) ? res : [])),
    [withRequest, setMap, inflightRef, withPreloader]
  )

  const getDatapointsByPanelIds = useCallback(
    (
      dashboardId: DashboardId,
      panels: PanelPool
    ): Promise<DatapointItemsResponse> =>
      withPreloader(() =>
        withRequest(async () => {
          const ids = (panels || []).map(p => p.id).filter(Boolean)
          if (!dashboardId || ids.length === 0) return { items: {} }

          const items: DatapointItemsResponse['items'] = {}
          const toFetch: number[] = []

          ids.forEach(id => {
            const k = keyFor(dashboardId, Number(id))
            const hit = mapRef.current?.[k]
            if (hit) {
              items[id] = Array.isArray(hit)
                ? hit.map(point => point?.attributes?.sensor_id)
                : []
            } else {
              toFetch.push(Number(id))
            }
          })

          await Promise.all(
            toFetch.map(async id => {
              const k = keyFor(dashboardId, id)

              const existing = inflightRef?.current?.get(k)
              if (existing) {
                const result = await existing
                items[id] = Array.isArray(result)
                  ? result.map(p => p?.attributes?.sensor_id)
                  : []
                return
              }

              const p = fetchDatapointsRaw(dashboardId, id)
                .then(result => {
                  if (result) {
                    setMap(prev => ({ ...prev, [k]: result }))
                    items[id] = result.map(p => p?.attributes?.sensor_id)
                  } else {
                    items[id] = []
                  }
                  return result
                })
                .finally(() => inflightRef?.current?.delete(k))

              inflightRef?.current?.set(k, p)
              await p
            })
          )

          return { items }
        })
      ).then(res => res ?? { items: {} }),
    [withRequest, setMap, inflightRef, withPreloader]
  )

  const update = useCallback(
    (id: DashboardId, data: DatapointUpdatePayload) =>
      withPreloader(() =>
        withRequest(async () => {
          await refreshToken()

          const body = {
            data: {
              attributes: { panel_id: data.panel_id },
              sensor_ids: data.sensor_ids || [],
            },
          }
          const url = `${REACT_APP_API_URL}/dashboard/${id}/panel/${data.panel_id}/datapoint`
          const options = {
            method: 'PATCH',
            headers: { ...authHeader(), 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          }
          const response = await fetch(url, options)

          if (!response.ok) {
            const text = await response.text()
            throw {
              message: text || `HTTP ${response.status}`,
              status: response.status,
            }
          }
          await response.json().catch(() => null)
          invalidate(id, data.panel_id)
          notifySuccess(LC.DATAPOINT_UPDATED)
          return true
        })
      ),
    [withRequest, invalidate, withPreloader]
  )

  return {
    getDatapoints,
    getDatapointsByPanelIds,
    update,
  }
}
